import { defineStore } from "pinia"
import { type cacheType, store, debounce, getKeyList } from "../utils"
import { constantMenus } from "@/router"
import { useMultiTagsStoreHook } from "./multiTags"
import type { PermissionItem } from "@/api/teacher"

export const usePermissionStore = defineStore({
  id: "pure-permission",
  state: () => ({
    // 静态路由生成的菜单
    constantMenus: constantMenus || [],
    // 整体路由生成的菜单（静态、动态）
    wholeMenus: [] as any[],
    // 整体路由（一维数组格式）
    flatteningRoutes: [] as any[],
    // 缓存页面keepAlive
    cachePageList: [] as string[],
    // 权限数据扩展
    permissions: [] as PermissionItem[],
    buttonPermissions: {} as Record<string, string[]>,
    // 菜单是否已初始化标志
    menuInitialized: false
  }),

  getters: {
    /** 获取所有权限 */
    getAllPermissions(): PermissionItem[] {
      return this.permissions
    },

    /** 获取指定页面的按钮权限 */
    getPageButtonPermissions() {
      return (routePath: string): string[] => {
        return this.buttonPermissions[routePath] || []
      }
    },

    /** 检查是否有指定权限 */
    hasPermission() {
      return (perms: string): boolean => {
        return this.permissions.some((permission) => permission.perms === perms)
      }
    },

    /** 检查是否有指定的按钮权限 */
    hasButtonPermission() {
      return (routePath: string, buttonPerms: string | string[]): boolean => {
        const pagePerms = this.buttonPermissions[routePath] || []
        if (Array.isArray(buttonPerms)) {
          return buttonPerms.every((perm) => pagePerms.includes(perm))
        }
        return pagePerms.includes(buttonPerms)
      }
    }
  },

  actions: {
    /** 组装整体路由生成的菜单 */
    handleWholeMenus(routes: any, forceUpdate = false) {
      console.log("🔧 handleWholeMenus 被调用", {
        routesLength: routes?.length,
        forceUpdate,
        menuInitialized: this.menuInitialized,
        currentMenusLength: this.wholeMenus.length
      })

      // 如果菜单已初始化且不是强制更新，则跳过
      if (this.menuInitialized && !forceUpdate) {
        console.log("菜单已初始化且非强制更新，跳过")
        return
      }

      if (!routes || !Array.isArray(routes)) {
        console.warn("传入的routes无效:", routes)
        this.wholeMenus = []
        this.menuInitialized = true
        return
      }

      // 检测路由结构
      if (routes.length > 0 && routes[0]?.children && routes[0].children.length > 0) {
        console.log("检测到层级菜单结构")
        this.wholeMenus = routes
      } else {
        console.log("检测到扁平菜单结构，直接设置")
        // 扁平菜单结构，直接设置
        this.wholeMenus = routes
      }

      this.menuInitialized = true
      console.log("🎯 菜单设置完成，最终长度:", this.wholeMenus.length)
      console.log("🎯 菜单内容:", this.wholeMenus)
    },

    /** 从权限数据构建菜单树 */
    buildMenusFromPermissions(permissions: PermissionItem[]) {
      // 过滤出菜单权限（目录和页面）
      const menuPermissions = permissions.filter(
        (permission) => permission.menuType === "M" || permission.menuType === "C"
      )

      // 找到根菜单
      const rootMenus = menuPermissions.filter((permission) => !permission.pid || permission.pid === 0)

      // 构建菜单树
      const menuTree = rootMenus.map((rootMenu) => this.buildMenuTree(rootMenu, menuPermissions))

      // 设置到wholeMenus
      this.wholeMenus = menuTree
    },

    /** 递归构建菜单节点 */
    buildMenuNode(permission: PermissionItem, permissionMap: Map<number, PermissionItem>): any {
      const node = {
        path: permission.routePath || this.generatePathFromPerms(permission.perms),
        name: permission.routeName || permission.menuName,
        meta: {
          title: permission.menuName,
          icon: permission.icon || "ep:menu",
          showLink: true,
          rank: permission.sort || 99,
          roles: ["teacher"]
        },
        children: []
      }

      // 查找子权限
      const children = []
      permissionMap.forEach((childPermission) => {
        if (childPermission.pid === permission.id && childPermission.hidden !== 1) {
          const childNode = this.buildMenuNode(childPermission, permissionMap)
          if (childNode) {
            children.push(childNode)
          }
        }
      })

      // 按sort排序
      children.sort((a, b) => (a.meta.rank || 99) - (b.meta.rank || 99))

      if (children.length > 0) {
        node.children = children
      }

      return node
    },

    /** 根据权限标识生成路径 */
    generatePathFromPerms(perms?: string): string {
      if (!perms) return "/unknown"
      return "/" + perms.replace(/:/g, "/")
    },

    /** 缓存页面keepAlive */
    cacheOperate({ mode, name }: cacheType) {
      const delIndex = this.cachePageList.findIndex((v) => v === name)
      switch (mode) {
        case "refresh":
          this.cachePageList = this.cachePageList.filter((v) => v !== name)
          break
        case "add":
          this.cachePageList.push(name)
          break
        case "delete":
          delIndex !== -1 && this.cachePageList.splice(delIndex, 1)
          break
      }
      /** 监听缓存页面是否存在于标签页，不存在则删除 */
      debounce(() => {
        let cacheLength = this.cachePageList.length
        const nameList = getKeyList(useMultiTagsStoreHook().multiTags, "name")
        while (cacheLength > 0) {
          nameList.findIndex((v) => v === this.cachePageList[cacheLength - 1]) === -1 &&
            this.cachePageList.splice(cacheLength - 1, 1)
          cacheLength--
        }
      })()
    },
    /** 清空缓存页面keepAlive */
    clearAllCachePage() {
      this.wholeMenus = []
      this.cachePageList = []
      this.menuInitialized = false // 重置初始化标志
    },

    /** 设置扁平化路由 */
    setFlatteningRoutes(routes: any[]) {
      this.flatteningRoutes = routes
    },

    /** 设置权限数据 */
    setPermissions(permissions: PermissionItem[]) {
      this.permissions = permissions
      this.buildButtonPermissions(permissions)
    },

    /** 构建按钮权限映射 */
    buildButtonPermissions(permissions: PermissionItem[]) {
      const buttonPerms: Record<string, string[]> = {}

      // 找到所有功能类型的权限
      const functionPermissions = permissions.filter((p) => p.menuType === "F")

      // 为每个页面权限查找对应的按钮权限
      const pagePermissions = permissions.filter((p) => p.menuType === "C")

      pagePermissions.forEach((pagePermission) => {
        const relatedButtons = functionPermissions
          .filter((funcPerm) => funcPerm.pid === pagePermission.id)
          .map((funcPerm) => funcPerm.perms)
          .filter(Boolean) as string[]

        if (relatedButtons.length > 0) {
          // 使用路由路径作为key
          const routePath = pagePermission.routePath || `/${pagePermission.perms?.replace(/:/g, "/")}`
          buttonPerms[routePath] = relatedButtons
        }
      })

      this.buttonPermissions = buttonPerms
    },

    /** 清空权限数据 */
    clearPermissions() {
      this.permissions = []
      this.buttonPermissions = {}
    }
  }
})

export function usePermissionStoreHook() {
  return usePermissionStore(store)
}
